/**
 * v1 Task, Initiative and Intake → Item (decision 0036, spec §2.9–2.10). One function per v1 record, used by the event
 * upcaster (TaskCreated, InitiativeCreated, IntakeCaptured) and the snapshot upcaster alike, so both hydration paths
 * build the same item. Values a snapshot can't know are constants on both paths.
 */
import type { Actor } from "../common"
import type {
  Assignee,
  Bet,
  Blocker,
  BlockReason,
  Edge,
  Item,
  ItemStatus,
  LegacyInitiativeShape,
  Resolution,
  Review,
  V1InitiativeStatus,
} from "../domain/item"
import type { Seq, ShortRef, TypeKey } from "../ids"
import type * as V1 from "../legacy/v1/index"
import { fromV1Meta, fromV1Placement, fromV1WorkRef, itemId } from "./common"

type V1Horizon = typeof V1.Horizon.Type
type V1Bet = typeof V1.Bet.Type
type V1Shape = typeof V1.InitiativeShape.Type
type V1Executor = V1.TaskExecutor
type V1Blocker = typeof V1.Blocker.Type
type V1BlockReason = typeof V1.BlockReason.Type
type V1Edge = typeof V1.Edge.Type
type V1Intake = V1.Intake
type V1IntakeStatus = typeof V1.IntakeStatus.Type

/** An item's status, pause and resolution for a v1 initiative's status and horizon (spec §2.6). */
export interface LegacyInitiativeState {
  readonly status: ItemStatus
  readonly paused: boolean
  readonly resolution: Resolution | null
}

/**
 * | v1 status         | now         | next | later   | paused | resolution |
 * | shaping           | todo        | todo | backlog | no     | —          |
 * | active            | in_progress | todo | backlog | no     | —          |
 * | paused            | todo        | todo | backlog | yes    | —          |
 * | done              | done        | done | done    | no     | —          |
 * | killed, abandoned | cancelled   | …    | …       | no     | wont_do    |
 */
export const legacyInitiativeState = (
  status: V1InitiativeStatus,
  horizon: V1Horizon,
): LegacyInitiativeState => {
  const planned = (now: ItemStatus): ItemStatus =>
    horizon === "later" ? "backlog" : horizon === "next" ? "todo" : now
  switch (status) {
    case "shaping":
      return { status: planned("todo"), paused: false, resolution: null }
    case "active":
      return { status: planned("in_progress"), paused: false, resolution: null }
    case "paused":
      return { status: planned("todo"), paused: true, resolution: null }
    case "done":
      return { status: "done", paused: false, resolution: null }
    case "killed":
    case "abandoned":
      return { status: "cancelled", paused: false, resolution: { _tag: "wont_do" } }
    default:
      return status satisfies never
  }
}

/** A v1 task's status: `impossible` became `cancelled` with resolution impossible{""} (the reason was not recorded). */
export const fromV1TaskStatus = (s: V1.TaskStatus): ItemStatus => (s === "impossible" ? "cancelled" : s)

/** The resolution a v1 task status implies. */
export const fromV1TaskResolution = (s: V1.TaskStatus): Resolution | null =>
  s === "impossible" ? { _tag: "impossible", reason: "" } : s === "cancelled" ? { _tag: "wont_do" } : null

export const executorToAssignee = (e: V1Executor): Assignee => {
  switch (e._tag) {
    case "worker":
      return { _tag: "worker", preset: e.preset }
    case "agent":
      return { _tag: "agent", agentId: e.agentId }
    default:
      return e satisfies never
  }
}

export const fromV1Blocker = (b: V1Blocker): Blocker => {
  switch (b._tag) {
    case "depends_on":
      return { _tag: "depends_on", itemId: itemId(b.taskId) }
    case "needs_input":
    case "missing_access":
    case "environment_broken":
    case "other":
      return b
    default:
      return b satisfies never
  }
}

export const fromV1BlockReason = (b: V1BlockReason): BlockReason => {
  switch (b._tag) {
    case "reported":
      return { _tag: "reported", blocker: fromV1Blocker(b.blocker), runId: b.runId }
    case "needs_human":
    case "question":
    case "impossible_claimed":
      return b
    default:
      return b satisfies never
  }
}

/** A v1 bet (the bet fields of an initiative's shape, without the tag). */
export const fromV1Bet = (b: V1Bet | (V1Bet & { readonly _tag: "bet" })): Bet => ({
  hypothesis: b.hypothesis,
  inputKpiId: b.inputKpiId,
  laggingKpiId: b.laggingKpiId,
  success: b.success,
  kill: b.kill,
  appetite: b.appetite,
  alternativesConsidered: b.alternativesConsidered.map(itemId),
  verdict: b.verdict,
  learningId: b.learningId,
})

export const fromV1Shape = (s: V1Shape): LegacyInitiativeShape => {
  switch (s._tag) {
    case "bet":
      return { _tag: "bet", ...fromV1Bet(s) }
    case "workstream":
      return s
    default:
      return s satisfies never
  }
}

/** The review an item without one gets (v1 initiatives and intake had none). */
export const LEGACY_REVIEW: Review = {
  stages: ["agent"],
  crossModel: true,
  maxRounds: 3,
  escalateTo: null,
  round: 0,
  stage: 0,
}

const EMPTY_PLACEMENT: Item["placement"] = { requires: {}, prefers: [], affinity: null, exclusive: [] }
const NO_HANDS: Item["hands"] = { agent: null, model: null, effort: null }

/** "ini_01k7…" → "ini-01k7…": a valid intent key derived from the id alone. */
export const intentKeyOfId = (id: string): Item["intentKey"] => id.replace("_", "-") as Item["intentKey"]

const specRev = (createdAt: number, createdBy: Actor): Item["specRev"] => ({
  n: 1 as Item["specRev"]["n"],
  at: createdAt,
  by: createdBy,
})

/** v1 Task → Item (spec §2.9 `fromV1Task`). The TaskCreated ancestry is dropped (derived from the tree now). */
export const fromV1Task = (t: V1.Task): Item => {
  const m = fromV1Meta(t)
  return {
    ownerId: t.ownerId,
    visibility: t.visibility,
    createdBy: m.createdBy,
    createdAt: t.createdAt,
    updatedBy: m.updatedBy,
    updatedAt: t.updatedAt,
    lastSeq: t.lastSeq,
    ref: t.ref,
    title: t.title,
    why: t.why,
    body: t.body,
    rank: t.rank,
    hold: t.hold,
    tags: t.tags,
    id: itemId(t.id),
    type: "task" as TypeKey,
    kind: t.kind,
    parentId: itemId(t.parent.id),
    goalIds: [],
    areaId: t.areaId,
    owner: t.assigneeId ? { _tag: "agent", agentId: t.assigneeId } : null,
    // also when the owner is null, so a later TaskAssigned agrees with the snapshot
    assignee: executorToAssignee(t.executor ?? { _tag: "worker", preset: null }),
    status: fromV1TaskStatus(t.status),
    resolution: fromV1TaskResolution(t.status),
    dueAt: null,
    notBefore: null,
    recurrence: null,
    checks: t.contract.checks,
    checksFrozenAt: t.contract.frozenAt,
    source: null,
    tainted: false,
    bet: null,
    request: t.request,
    blocked: t.blocked === null ? null : fromV1BlockReason(t.blocked),
    paused: t.paused,
    ...(t.pausedBy !== undefined ? { pausedBy: t.pausedBy } : {}),
    ...(t.pauseNote !== undefined ? { pauseNote: t.pauseNote } : {}),
    ...(t.closeEvidence !== undefined ? { closeEvidence: t.closeEvidence } : {}),
    intentKey: t.intentKey,
    review: t.review,
    placement: fromV1Placement(t.placement),
    hands: t.hands,
    paths: t.paths,
    integrations: t.integrations,
    attempts: t.attempts,
    retry: t.retry,
    activeRunId: t.activeRunId,
    lastRunId: t.lastRunId,
    supersedes: t.supersedes === null ? null : itemId(t.supersedes),
    specRev: specRev(t.createdAt, m.createdBy),
    startedAt: null,
    closedAt: null,
    overdueNoticed: null,
  }
}

/** What a legacy initiative's shape sets on its item (spec §2.6; shared with core's evolve). */
export interface LegacyShapeFacts {
  readonly type: TypeKey
  readonly bet: Bet | null
  readonly dueAt: number | null
  readonly definitionOfDone: readonly [string, ...Array<string>] | null
  readonly supportsKpiId: V1.KpiId | null
}

/** A bet sets `bet`; a workstream its due date, definition of done and supporting KPI. */
export const legacyShapeFacts = (shape: LegacyInitiativeShape): LegacyShapeFacts => {
  if (shape._tag === "bet") {
    const { _tag: _bet, ...bet } = shape
    return { type: "bet" as TypeKey, bet, dueAt: null, definitionOfDone: null, supportsKpiId: null }
  }
  return {
    type: "workstream" as TypeKey,
    bet: null,
    dueAt: shape.targetDate,
    definitionOfDone: shape.definitionOfDone,
    supportsKpiId: shape.supportsKpiId,
  }
}

/** v1 Initiative → a top-level item (spec §2.9 `fromV1Initiative`). */
export const fromV1Initiative = (i: typeof V1.Initiative.Type): Item => {
  const m = fromV1Meta(i)
  const state = legacyInitiativeState(i.status, i.horizon)
  const shape = legacyShapeFacts(fromV1Shape(i.shape))
  return {
    ownerId: i.ownerId,
    visibility: i.visibility,
    createdBy: m.createdBy,
    createdAt: i.createdAt,
    updatedBy: m.updatedBy,
    updatedAt: i.updatedAt,
    lastSeq: i.lastSeq,
    ref: i.ref,
    title: i.title,
    why: i.why,
    body: i.body,
    rank: i.rank,
    hold: i.hold,
    tags: i.tags,
    id: itemId(i.id),
    type: shape.type,
    kind: "work",
    parentId: null,
    goalIds: [...i.goalIds],
    areaId: i.areaId,
    owner: i.ownerAgentId ? { _tag: "agent", agentId: i.ownerAgentId } : null,
    assignee: null,
    status: state.status,
    resolution: state.resolution,
    dueAt: shape.dueAt,
    notBefore: null,
    recurrence: null,
    checks: [],
    checksFrozenAt: null,
    source: null,
    tainted: false,
    bet: shape.bet,
    request: null,
    blocked: null,
    paused: state.paused,
    pausedBy: null,
    pauseNote: null,
    intentKey: intentKeyOfId(i.id),
    review: LEGACY_REVIEW,
    placement: EMPTY_PLACEMENT,
    hands: NO_HANDS,
    paths: [],
    integrations: [],
    attempts: 0,
    retry: null,
    activeRunId: null,
    lastRunId: null,
    supersedes: i.supersedes === null ? null : itemId(i.supersedes),
    specRev: specRev(i.createdAt, m.createdBy),
    startedAt: i.startedAt,
    closedAt: i.closedAt,
    overdueNoticed: null,
    legacy: {
      initiative: {
        status: i.status,
        horizon: i.horizon,
        definitionOfDone: shape.definitionOfDone,
        supportsKpiId: shape.supportsKpiId,
      },
    },
  }
}

/** The one-line `why` of a captured item (intake had none): from its source alone. */
export const captureWhy = (source: V1Intake["source"]): string => {
  switch (source._tag) {
    case "web":
      return "Captured from the web"
    case "cli":
      return "Captured from the CLI"
    case "mcp":
      return `Captured from ${source.client} (MCP)`
    case "email":
      return "Captured by email"
    case "webhook":
      return "Captured from a webhook"
    case "agent":
      return "Captured by an agent"
    default:
      return source satisfies never
  }
}

/** How a resolved v1 intake item closes as an item: its resolution and the cancellation reason. */
export const intakeClosure = (
  status: V1IntakeStatus,
): { readonly resolution: Resolution; readonly reason: string } | null => {
  switch (status._tag) {
    case "new":
    case "triaged":
      return null
    case "linked": {
      const ref = status.ref
      return ref._tag === "task" || ref._tag === "initiative" || ref._tag === "intake"
        ? { resolution: { _tag: "superseded", by: itemId(ref.id) }, reason: `linked to ${ref.id}` }
        : { resolution: { _tag: "wont_do" }, reason: `linked to ${ref._tag} ${ref.id}` }
    }
    case "duplicate":
      return { resolution: { _tag: "duplicate", of: itemId(status.of) }, reason: "duplicate" }
    case "closed":
      return { resolution: { _tag: "wont_do" }, reason: status.reason }
    default:
      return status satisfies never
  }
}

/**
 * v1 Intake → a triage item (spec §2.9 `fromV1Intake`). `captureSeq` is the seq of its IntakeCaptured, which is also the
 * intake's `contentSeq`: the ref `C-<captureSeq>` comes out the same from the event and from the snapshot record.
 */
export const fromV1Intake = (x: V1Intake, captureSeq: number): Item => {
  const m = fromV1Meta(x)
  const closure = intakeClosure(x.status)
  return {
    ownerId: x.ownerId,
    visibility: x.visibility,
    createdBy: m.createdBy,
    createdAt: x.createdAt,
    updatedBy: m.updatedBy,
    updatedAt: x.updatedAt,
    lastSeq: x.lastSeq,
    ref: `C-${captureSeq}` as ShortRef,
    title: x.title,
    why: captureWhy(x.source),
    body: x.body,
    rank: "U",
    hold: { _tag: "none" },
    tags: [],
    id: itemId(x.id),
    type: x.kind as TypeKey,
    kind: "work",
    parentId: null,
    goalIds: [],
    areaId: x.suggestedAreaId,
    owner: null,
    assignee: null,
    status: closure ? "cancelled" : "triage",
    resolution: closure ? closure.resolution : null,
    dueAt: null,
    notBefore: null,
    recurrence: null,
    checks: [],
    checksFrozenAt: null,
    source: x.source,
    tainted: x.tainted,
    bet: null,
    request: null,
    blocked: null,
    paused: false,
    pausedBy: null,
    pauseNote: null,
    intentKey: intentKeyOfId(x.id),
    review: LEGACY_REVIEW,
    placement: EMPTY_PLACEMENT,
    hands: NO_HANDS,
    paths: [],
    integrations: [],
    attempts: 0,
    retry: null,
    activeRunId: null,
    lastRunId: null,
    supersedes: null,
    specRev: specRev(x.createdAt, m.createdBy),
    startedAt: null,
    closedAt: null,
    overdueNoticed: null,
    contentSeq: captureSeq as Seq,
    legacy: { intake: { kind: x.kind, attachments: [...x.attachments] } },
  }
}

export const fromV1Edge = (e: V1Edge): Edge => ({
  ...fromV1Meta(e),
  from: fromV1WorkRef(e.from),
  to: fromV1WorkRef(e.to),
})
